import type { ApiClient } from "@/shared/api/baseClient";
import type {
  SupportedLanguage,
  Prefecture,
  City,
  Location,
  Station,
  GeocodeRequest,
  GeocodeResult,
  AddressValidationRequest,
  AddressValidationResult,
} from "../types/geo.types";

// API response wrapper types (backend returns wrapped objects)
interface PrefecturesResponse {
  prefectures: Prefecture[];
}

interface CitiesResponse {
  cities: City[];
}

interface LocationsResponse {
  locations: Location[];
}

interface StationsResponse {
  stations: Station[];
}

export class GeoApi {
  constructor(private client: ApiClient) {}

  async fetchPrefectures(lang?: SupportedLanguage): Promise<Prefecture[]> {
    const response = await this.client.get<PrefecturesResponse>(
      "/geo/prefectures",
      { lang },
    );
    return response.prefectures ?? [];
  }

  async fetchCities(
    prefectureCode: number,
    lang?: SupportedLanguage,
  ): Promise<City[]> {
    const response = await this.client.get<CitiesResponse>("/geo/cities", {
      prefecture_code: prefectureCode,
      lang,
    });
    return response.cities ?? [];
  }

  async fetchLocations(options?: {
    citySlug?: string;
    cityId?: string;
    lang?: SupportedLanguage;
  }): Promise<Location[]> {
    // Convert to snake_case for backend
    const params = {
      city_slug: options?.citySlug,
      city_id: options?.cityId,
      lang: options?.lang,
    };
    const response = await this.client.get<LocationsResponse>(
      "/geo/locations",
      params,
    );
    return response.locations ?? [];
  }

  async fetchStations(options?: {
    citySlug?: string;
    cityId?: string;
    locationId?: string;
    lang?: SupportedLanguage;
  }): Promise<Station[]> {
    // Convert to snake_case for backend
    const params = {
      city_slug: options?.citySlug,
      city_id: options?.cityId,
      location_id: options?.locationId,
      lang: options?.lang,
    };
    const response = await this.client.get<StationsResponse>(
      "/geo/stations",
      params,
    );
    return response.stations ?? [];
  }

  async searchStations(
    query: string,
    options?: {
      citySlug?: string;
      limit?: number;
      lang?: SupportedLanguage;
    },
  ): Promise<Station[]> {
    const params = {
      q: query,
      city_slug: options?.citySlug,
      limit: options?.limit,
      lang: options?.lang,
    };
    const response = await this.client.get<StationsResponse>(
      "/geo/stations/search",
      params,
    );
    return response.stations ?? [];
  }

  async findNearbyStations(
    lat: number,
    lon: number,
    options?: {
      radiusM?: number;
      citySlug?: string;
      limit?: number;
      lang?: SupportedLanguage;
    },
  ): Promise<Station[]> {
    const params = {
      lat,
      lon,
      radius_m: options?.radiusM,
      city_slug: options?.citySlug,
      limit: options?.limit,
      lang: options?.lang,
    };
    const response = await this.client.get<StationsResponse>(
      "/geo/stations/nearby",
      params,
    );
    return response.stations ?? [];
  }

  geocodeAddress(request: GeocodeRequest): Promise<GeocodeResult> {
    return this.client.post("/geo/geocode", request);
  }

  validateAddress(
    request: AddressValidationRequest,
  ): Promise<AddressValidationResult> {
    return this.client.post("/geo/validate-address", request);
  }
}

export const calculateWalkMinutes = (distanceM: number): number => {
  const walkSpeedMetersPerMinute = 80;
  return Math.ceil(distanceM / walkSpeedMetersPerMinute);
};

export const calculateDistanceMeters = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number => {
  const R = 6371e3; // metres
  const φ1 = (lat1 * Math.PI) / 180; // φ, λ in radians
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const d = R * c; // in metres
  return d;
};
